import { ReactNode, useCallback, useEffect, useState } from "react";
import { Icon } from "@/components";
import { Badge } from "@/ui";
import { AlgoliaOrder } from "@/types";
import { KANBAN_LANES } from "config/constant";
import { useOrdersData } from "../context/OrdersDataContext";
import badgeStyles from "../orderBadges.module.scss";
import { normalizeStatusTone } from "../orderVisualTokens";
import KanbanCard from "./KanbanCard";
import KanbanCardSkeleton from "./KanbanCardSkeleton";
import KanbanModal from "./KanbanModal";
import styles from "./KanbanSection.module.scss";

/** Placeholder card counts per lane (Processing → Cancelled). */
export const KANBAN_SKELETON_COUNTS = [4, 3, 2, 2, 5, 3] as const;

export type KanbanLaneData = {
    name: string;
    color: string;
    orders: AlgoliaOrder[];
};

/** Pure lane bucketing — shared by the connected section and Storybook. */
export const buildKanbanLanes = (
    orders: AlgoliaOrder[] | null | undefined,
): KanbanLaneData[] =>
    Object.entries(KANBAN_LANES).map(([laneName, laneProps]) => {
        const statuses = Array.isArray(laneProps.status)
            ? laneProps.status
            : [laneProps.status];
        const laneOrders = (orders || []).filter(order =>
            statuses.includes(order.status),
        );

        return {
            name: laneName,
            color: laneProps.color,
            orders: laneOrders,
        };
    });

export type KanbanSectionViewProps = {
    lanes: KanbanLaneData[];
    renderCard: (order: AlgoliaOrder, accentClass: string) => ReactNode;
    /** Initial load — show shimmer cards instead of empty lanes. */
    loading?: boolean;
    /** Cards per lane while loading; defaults to KANBAN_SKELETON_COUNTS. */
    skeletonCounts?: readonly number[];
};

/**
 * Presentational kanban board (lanes chrome).
 * Scroll / modal / GraphQL stay in the default `KanbanSection` export.
 */
export const KanbanSectionView = ({
    lanes,
    renderCard,
    loading = false,
    skeletonCounts = KANBAN_SKELETON_COUNTS,
}: KanbanSectionViewProps) => (
    <div
        aria-busy={loading || undefined}
        aria-label={loading ? "Loading orders" : undefined}
        className={styles.board}
    >
        {lanes.map((lane, laneIndex) => {
            const placeholderCount = skeletonCounts[laneIndex] ?? 2;

            return (
                <div className={styles.lane} key={lane.name}>
                    <h3 className={styles.laneHeader}>
                        <span aria-hidden className={styles.laneIcon}>
                            <Icon icon="dots" />
                        </span>
                        <Badge
                            className={`${styles.laneBadge} ${badgeStyles.statusTone}`}
                            data-tone={normalizeStatusTone(lane.color)}
                            variant="neutral"
                        >
                            {lane.name}
                        </Badge>
                        <span className={styles.laneCount}>
                            {loading ? "…" : lane.orders.length}
                        </span>
                    </h3>
                    <div className={styles.laneCards}>
                        {loading
                            ? Array.from(
                                  { length: placeholderCount },
                                  (_, i) => (
                                      <KanbanCardSkeleton
                                          key={`skel-${lane.name}-${i}`}
                                      />
                                  ),
                              )
                            : lane.orders.map((order, cardKey) => (
                                  <div key={cardKey}>
                                      {renderCard(order, lane.color)}
                                  </div>
                              ))}
                    </div>
                </div>
            );
        })}
    </div>
);

const KanbanSection = () => {
    const { orders, fetchMore, hasNextPage, loading } = useOrdersData();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [algoliaOrder, setAlgoliaOrder] = useState<AlgoliaOrder>();

    const handleModalToggle = (order: AlgoliaOrder) => {
        setIsModalOpen(prev => !prev);
        setAlgoliaOrder(order);
    };

    const handleWindowScroll = useCallback(() => {
        if (!hasNextPage || loading) return;

        const { scrollTop, scrollHeight, clientHeight } =
            document.documentElement;
        const distanceToBottom = scrollHeight - scrollTop - clientHeight;

        // threshold to trigger the load before reaching the end
        if (distanceToBottom < 200) {
            fetchMore();
        }
    }, [fetchMore, hasNextPage, loading]);

    useEffect(() => {
        window.addEventListener("scroll", handleWindowScroll);
        return () => {
            window.removeEventListener("scroll", handleWindowScroll);
        };
    }, [handleWindowScroll]);

    const lanes = buildKanbanLanes(orders);
    // Primary fetch clears orders + sets loading; avoid stale cards during refetch.
    const isPrimaryLoading = loading && !orders?.length;

    return (
        <>
            <KanbanSectionView
                lanes={lanes}
                loading={isPrimaryLoading}
                renderCard={(order, accentClass) => (
                    <KanbanCard
                        accentClass={accentClass}
                        handleModalToggle={handleModalToggle}
                        order={order}
                    />
                )}
            />
            {algoliaOrder ? (
                <KanbanModal
                    algoliaOrder={algoliaOrder}
                    handleModalToggle={handleModalToggle}
                    isModalOpen={isModalOpen}
                />
            ) : null}
        </>
    );
};

export default KanbanSection;
